import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { useUpdateAllocationAmountForDealOpportunityGp } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import {
  getGetDealTrackingForGpQueryKey,
  useUpdateDealOpportunityTracking,
} from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { type UpdateDealOpportunityTrackingRequest } from "@vega-api/generated/types/updateDealOpportunityTrackingRequest";
import { useRevertEditOnError } from "@vega-core/components/DataGrid/useRevertEditOnError";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { TRACK_ALLOCATION_COL_IDS } from "./useTrackAllocationColumns";

export const useTrackAllocationEdit = (dealId: string) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const { captureCellChangeEvent } =
    useRevertEditOnError<DealTrackingLimitedPartnerBreakdown>();

  const onSuccess = () => {
    showToast({ variant: "success", title: m.status_success() });
    queryClient.invalidateQueries({
      queryKey: getGetDealTrackingForGpQueryKey(dealId),
    });
  };

  const onError = (error: ErrorType<unknown>) => {
    showErrorToast(error, { defaultMessage: m.dataGrid_updateError() });
  };

  const { mutate: updateTracking } = useUpdateDealOpportunityTracking({
    mutation: { onSuccess, onError },
  });

  const { mutate: updateAllocationAmount } =
    useUpdateAllocationAmountForDealOpportunityGp({
      mutation: {
        onSuccess,
        onError,
      },
    });

  const handleCellValueChanged = captureCellChangeEvent((event, revertEdit) => {
    const row = event.data;
    const dealOpportunityId = row.dealOpportunityId;
    const colId = event.column.getColId();

    const patchTracking = (data: UpdateDealOpportunityTrackingRequest) => {
      updateTracking(
        { dealOpportunityId, data },
        {
          onError: revertEdit,
        },
      );
    };

    switch (colId) {
      case TRACK_ALLOCATION_COL_IDS.lpTicketSizeMin:
      case TRACK_ALLOCATION_COL_IDS.lpTicketSizeMax:
        if (!row.ticketSize) return;
        patchTracking({
          ticketSize: {
            minimum: row.ticketSize.minimum,
            maximum: row.ticketSize.maximum,
          },
        });
        break;
      case TRACK_ALLOCATION_COL_IDS.indicativeAmount:
        if (!row.indicativeAmount) return;
        patchTracking({ indicationOfInterestAmount: row.indicativeAmount });
        break;
      case TRACK_ALLOCATION_COL_IDS.gpIndicativeOrder:
        if (!row.gpIndicativeOrder) return;
        patchTracking({ gpIndicativeOrder: row.gpIndicativeOrder });
        break;
      case TRACK_ALLOCATION_COL_IDS.estimatedTicketSizeMin:
        if (!row.estimatedTicketSizeMin) return;
        patchTracking({
          estimatedTicketSizeMin: row.estimatedTicketSizeMin,
        });
        break;
      case TRACK_ALLOCATION_COL_IDS.estimatedTicketSizeMax:
        if (!row.estimatedTicketSizeMax) return;
        patchTracking({
          estimatedTicketSizeMax: row.estimatedTicketSizeMax,
        });
        break;
      case TRACK_ALLOCATION_COL_IDS.probability:
        if (row.probability == null) return;
        patchTracking({
          probability: row.probability,
        });
        break;
      case TRACK_ALLOCATION_COL_IDS.allocationAmount:
        if (!row.allocationAmount) return;
        updateAllocationAmount(
          {
            dealOpportunityId,
            data: { allocationAmount: row.allocationAmount },
          },
          { onError: revertEdit },
        );
        break;
      default:
        return;
    }
  });

  return { handleCellValueChanged };
};
